import { useEffect, useState } from 'react'
import type { ImageSize } from '@/utils/image'
import { isPreloadSettled, preloadImage } from '@/utils/imageCache'
import { handleProductImageError, resolveProductImage, resolveProductName } from '../utils/omp'

interface OmpProductImageProps {
  source: any
  size?: ImageSize
  useSubscription?: boolean
  alt?: string
  className?: string
}

// Single site for every product thumbnail in the portal.
//
// The <img> is mounted only once its bitmap is decoded in the shared cache. A thumbnail
// that loads while the card's entrance animation is compositing can otherwise finish
// decoding without the browser repainting the (already laid out) image box — it stays
// blank until some unrelated invalidation, e.g. a hover, forces a repaint. Mounting a
// new node after decode cannot be missed that way: insertion always paints.
const OmpProductImage = ({ source, size = 'large', useSubscription = false, alt, className }: OmpProductImageProps) => {
  const src = resolveProductImage(source, useSubscription, size)
  const [decoded, setDecoded] = useState(() => isPreloadSettled(src))

  useEffect(() => {
    if (isPreloadSettled(src)) {
      setDecoded(true)
      return
    }
    setDecoded(false)
    let active = true
    const load = async () => {
      await preloadImage(src)
      if (active) {
        setDecoded(true)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [src])

  if (!decoded) {
    return null
  }

  return (
    <img
      src={src}
      alt={alt ?? resolveProductName(source)}
      className={className}
      decoding="sync"
      fetchPriority="high"
      onError={e => handleProductImageError(e, source, useSubscription)}
    />
  )
}

export default OmpProductImage
